// Site 管理页（Task 16.2）——
//   - 列表 + 状态徽标
//   - 新建（弹窗内一次性显示 secret_key 明文）
//   - 重置 secret（admin+ 权限；服务端会拒绝低权限）
//   - 删除（吊销）

import { useEffect, useState } from 'react';
import {
  createSite,
  deleteSite,
  listSites,
  rotateSecret,
  type SiteRecord,
} from '../lib/api';

export function Sites(): JSX.Element {
  const [items, setItems] = useState<SiteRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [revealed, setRevealed] = useState<{ id: string; secret: string } | null>(null);

  async function refresh(): Promise<void> {
    setLoading(true);
    setError(null);
    try {
      const r = await listSites();
      setItems(r.sites);
    } catch (ex) {
      setError(ex instanceof Error ? ex.message : 'failed to load');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
  }, []);

  async function onRotate(id: string): Promise<void> {
    if (!confirm('重置 Secret 后旧 Secret 24h 平滑期内仍可用。确认继续？')) return;
    try {
      const r = await rotateSecret(id);
      setRevealed({ id, secret: r.secret_key_plaintext });
      await refresh();
    } catch (ex) {
      alert(ex instanceof Error ? ex.message : 'failed');
    }
  }

  async function onDelete(id: string): Promise<void> {
    if (!confirm('删除 Site 将在 60s 内全网吊销 site_key。确认继续？')) return;
    try {
      await deleteSite(id);
      await refresh();
    } catch (ex) {
      alert(ex instanceof Error ? ex.message : 'failed');
    }
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2 style={{ margin: 0 }}>Sites</h2>
        <button onClick={() => setShowCreate(true)} style={primaryBtn}>
          新建 Site
        </button>
      </div>

      {error && <div style={errorBox}>{error}</div>}
      {loading && <div style={{ marginTop: 12, color: '#64748b' }}>加载中…</div>}

      <table style={{ width: '100%', marginTop: 16, borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            {['名称', 'site_key', 'secret 掩码', '状态', '操作'].map((h) => (
              <th
                key={h}
                style={{ textAlign: 'left', padding: '8px 6px', fontSize: 12, color: '#475569', borderBottom: '1px solid #e2e8f0' }}
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {items.map((s) => (
            <tr key={s.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
              <td style={cellStyle}>{s.name}</td>
              <td style={{ ...cellStyle, fontFamily: 'monospace' }}>{s.site_key}</td>
              <td style={{ ...cellStyle, fontFamily: 'monospace' }}>{s.secret_key_mask}</td>
              <td style={cellStyle}>
                <span
                  style={{
                    display: 'inline-block',
                    padding: '2px 8px',
                    borderRadius: 12,
                    fontSize: 11,
                    background: s.status === 'active' ? '#dcfce7' : '#fee2e2',
                    color: s.status === 'active' ? '#166534' : '#991b1b',
                  }}
                >
                  {s.status}
                </span>
              </td>
              <td style={cellStyle}>
                <button onClick={() => onRotate(s.id)} style={smallBtn}>重置 Secret</button>
                <button onClick={() => onDelete(s.id)} style={{ ...smallBtn, color: '#991b1b' }}>
                  吊销
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {showCreate && <CreateModal onClose={() => setShowCreate(false)} onCreated={refresh} onRevealed={setRevealed} />}
      {revealed && (
        <SecretReveal
          secret={revealed.secret}
          onClose={() => setRevealed(null)}
        />
      )}
    </div>
  );
}

function CreateModal(props: {
  onClose: () => void;
  onCreated: () => Promise<void>;
  onRevealed: (r: { id: string; secret: string }) => void;
}): JSX.Element {
  const [name, setName] = useState('');
  const [origins, setOrigins] = useState('https://example.com');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function onSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      const allowed_origins = origins
        .split(/[\n,]/)
        .map((s) => s.trim())
        .filter(Boolean)
        .slice(0, 10);
      const r = await createSite({ name, allowed_origins });
      props.onRevealed({ id: r.site.id, secret: r.secret_key_plaintext });
      await props.onCreated();
      props.onClose();
    } catch (ex) {
      setErr(ex instanceof Error ? ex.message : 'failed');
    } finally {
      setBusy(false);
    }
  }
  return (
    <Modal onClose={props.onClose} title="新建 Site">
      <form onSubmit={onSubmit}>
        <label style={labelStyle}>
          名称
          <input style={inputStyle} value={name} onChange={(e) => setName(e.target.value)} required />
        </label>
        <label style={labelStyle}>
          允许来源（逗号或换行分隔，最多 10 个）
          <textarea
            style={{ ...inputStyle, minHeight: 80, fontFamily: 'monospace' }}
            value={origins}
            onChange={(e) => setOrigins(e.target.value)}
          />
        </label>
        {err && <div style={errorBox}>{err}</div>}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
          <button type="button" onClick={props.onClose} style={ghostBtn}>取消</button>
          <button type="submit" disabled={busy} style={primaryBtn}>
            {busy ? '创建中…' : '创建'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function SecretReveal(props: { secret: string; onClose: () => void }): JSX.Element {
  return (
    <Modal onClose={props.onClose} title="Secret Key (仅显示一次)">
      <p style={{ color: '#475569', fontSize: 13 }}>
        请立即复制并妥善保管。关闭弹窗后将无法再次查看，但可通过"重置 Secret"重新签发。
      </p>
      <code
        style={{
          display: 'block',
          padding: 12,
          background: '#f1f5f9',
          borderRadius: 6,
          fontFamily: 'monospace',
          wordBreak: 'break-all',
        }}
      >
        {props.secret}
      </code>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
        <button
          onClick={() => navigator.clipboard?.writeText(props.secret)}
          style={ghostBtn}
        >
          复制
        </button>
        <button onClick={props.onClose} style={primaryBtn}>关闭</button>
      </div>
    </Modal>
  );
}

function Modal(props: { onClose: () => void; title: string; children: React.ReactNode }): JSX.Element {
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,23,42,0.4)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 100,
      }}
      onClick={props.onClose}
    >
      <div
        style={{
          background: 'white',
          padding: 24,
          borderRadius: 8,
          minWidth: 400,
          maxWidth: 600,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 style={{ margin: '0 0 16px 0' }}>{props.title}</h3>
        {props.children}
      </div>
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 10px',
  border: '1px solid #cbd5e1',
  borderRadius: 6,
  fontSize: 14,
  boxSizing: 'border-box',
};
const labelStyle: React.CSSProperties = {
  display: 'block',
  marginBottom: 12,
  fontSize: 12,
  color: '#475569',
};
const cellStyle: React.CSSProperties = { padding: '8px 6px', fontSize: 13 };
const primaryBtn: React.CSSProperties = {
  background: '#2563eb',
  color: 'white',
  border: 0,
  borderRadius: 6,
  padding: '8px 12px',
  fontSize: 13,
  cursor: 'pointer',
};
const ghostBtn: React.CSSProperties = {
  background: 'transparent',
  border: '1px solid #cbd5e1',
  borderRadius: 6,
  padding: '8px 12px',
  fontSize: 13,
  cursor: 'pointer',
};
const smallBtn: React.CSSProperties = {
  background: 'transparent',
  border: 0,
  color: '#2563eb',
  cursor: 'pointer',
  fontSize: 12,
  marginRight: 8,
};
const errorBox: React.CSSProperties = {
  marginTop: 12,
  padding: 8,
  fontSize: 12,
  color: '#991b1b',
  background: '#fee2e2',
  borderRadius: 6,
};
